// frontend/src/pages/LoginPage.js
import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  TextField,
  Button,
  Typography,
  Paper,
  InputAdornment,
  IconButton,
  Alert,
  useTheme,
  alpha
} from '@mui/material';
import { useNavigate, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useAuth } from '../context/AuthContext';
import PageTransition from '../components/animations/PageTransition';
import { TRANSLATIONS } from '../constants/translations';

// Icons
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import LockIcon from '@mui/icons-material/Lock';
import PersonIcon from '@mui/icons-material/Person';

const LoginPage = () => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [formError, setFormError] = useState('');
  const { login, currentUser, error } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();

  // Reset form state on mount or when navigating to login
  useEffect(() => {
    setUsername('');
    setPassword('');
    setFormError('');
    setShowPassword(false);
  }, [location.pathname]);

  // Redirect if already logged in
  useEffect(() => {
    if (currentUser) {
      const from = location.state?.from?.pathname || '/dashboard';
      navigate(from);
    }
  }, [currentUser, navigate, location]);


  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormError('');

    if (!username.trim()) {
      setFormError(TRANSLATIONS.VALIDATION.REQUIRED_FIELD);
      return;
    }

    if (!password) {
      setFormError(TRANSLATIONS.VALIDATION.REQUIRED_FIELD);
      return;
    }

    try {
      await login(username, password);
      // Successful login will trigger the useEffect above
    } catch (err) {
      setFormError(err.message || TRANSLATIONS.AUTH.LOGIN_FAILED);

    }
  };

  return (
    <PageTransition>
      <Box
        sx={{
          minHeight: 'calc(100vh - 64px)',
          py: 8,
          display: 'flex',
          alignItems: 'center',
          background: `radial-gradient(circle at 30% 40%, ${alpha(theme.palette.primary.main, 0.15)} 0%, transparent 70%), 
                       radial-gradient(circle at 80% 10%, ${alpha(theme.palette.secondary.main, 0.1)} 0%, transparent 50%)`,
        }}
      >
        <Container maxWidth="sm">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <Paper
              elevation={0}
              sx={{
                p: 4,
                borderRadius: 3,
                background: theme.palette.background.paper,
                backdropFilter: 'blur(10px)',
                border: `1px solid ${theme.palette.divider}`,
                boxShadow: '0 10px 30px rgba(0, 0, 0, 0.1)'
              }}
            >
              <Box sx={{ textAlign: 'center', mb: 4 }}>
                <Typography 
                  variant="h4" 
                  component="h1" 
                  sx={{ 
                    fontWeight: 700,
                    mb: 1,
                    color: theme.palette.primary.main
                  }}
                >
                  Einloggen
                </Typography>
                <Typography 
                  variant="body1" 
                  color="text.secondary"
                >
                </Typography>
              </Box>

              {(error || formError) && (
                <Alert 
                  severity="error" 
                  sx={{ 
                    mb: 3, 
                    bgcolor: alpha(theme.palette.error.main, 0.1),
                    color: theme.palette.error.main,
                    border: `1px solid ${alpha(theme.palette.error.main, 0.2)}`
                  }}
                >
                  Fehler: {formError}
                </Alert>
              )}

              <Box component="form" onSubmit={handleSubmit} noValidate>
                <TextField
                  margin="normal"
                  required
                  fullWidth
                  id="username"
                  label="Nutzername"
                  name="username"
                  autoComplete="username"
                  autoFocus
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <PersonIcon sx={{ color: theme.palette.text.secondary }} />
                      </InputAdornment>
                    ),
                  }}
                />
                
                <TextField
                  margin="normal"
                  required
                  fullWidth
                  name="password"
                  label="Passwort"
                  type={showPassword ? 'text' : 'password'}
                  id="password"
                  autoComplete="current-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <LockIcon sx={{ color: theme.palette.text.secondary }} />
                      </InputAdornment>
                    ),
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          aria-label="Ein/Ausblenden des Passworts"
                          onClick={() => setShowPassword(!showPassword)}
                          edge="end"
                        >
                          {showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                        </IconButton>
                      </InputAdornment>
                    )
                  }}
                />

                <motion.div whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
                  <Button
                    type="submit"
                    fullWidth
                    variant="contained"
                    sx={{
                      mt: 3,
                      mb: 3,
                      py: 1.5,
                      borderRadius: theme.shape.borderRadius,
                      fontWeight: 600
                    }}
                  >
                    Anmelden
                  </Button>
                </motion.div>
              </Box>
            </Paper>
          </motion.div>
        </Container>
      </Box>
    </PageTransition>
  );
};

export default LoginPage;